import React, { useState } from "react";
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import FormatUrl from '../../utils/UrlFormatter';

const PaymentComponent = ({ clinicPatientId, amount }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");



  const handlePayment = async () => {
    try {
        setLoading(true);

        // Step 1: Fetch CSRF Token
        const csrfApiUrl = FormatUrl("/csrf-token/");
        const csrfResponse = await fetch(csrfApiUrl, {
            method: "GET",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
        });

        if (!csrfResponse.ok) {
            throw new Error("Failed to fetch CSRF token");
        }

        const csrfData = await csrfResponse.json();
        const csrfToken = csrfData.csrfToken;

        // Step 2: Create PaymentIntent
        const paymentApiUrl = FormatUrl("/create-payment/");
        const payload = {
            clinic_patient_id: clinicPatientId, // Combine clinic and patient ID as per backend
            amount,
            currency_code: "usd", // Update key to currency_code
        };

        const response = await fetch(paymentApiUrl, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "X-CSRFToken": csrfToken,
            },
            credentials: "include",
            body: JSON.stringify(payload),
        });

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Failed to create PaymentIntent: ${errorText}`);
        }

        const { intent_id } = await response.json();
        const payment_intent_id = intent_id; // Use intent_id from the backend response

        // Step 3: Create PaymentMethod using Stripe.js
        const { paymentMethod, error: paymentMethodError } = await stripe.createPaymentMethod({
            type: "card",
            card: elements.getElement(CardElement), // Retrieve the CardElement
        });

        if (paymentMethodError) {
            throw new Error(`Failed to create payment method: ${paymentMethodError.message}`);
        }

        const stripePaymentMethodId = paymentMethod.id;


        // Step 4: Confirm Payment via Backend
        const confirmApiUrl = FormatUrl("/confirm-payment/");
        const confirmPayload = {
            payment_intent_id,
            payment_method: stripePaymentMethodId, // Add the payment method ID
        };
    
        const confirmResponse = await fetch(confirmApiUrl, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "X-CSRFToken": csrfToken,
            },
            credentials: "include",
            body: JSON.stringify(confirmPayload),
        });

        if (!confirmResponse.ok) {
            const errorText = await confirmResponse.text();
            throw new Error(`Failed to confirm payment: ${errorText}`);
        }

        const confirmData = await confirmResponse.json();
        if (confirmData.status !== "succeeded") {
            throw new Error("Payment confirmation failed.");
        }

        // Payment successful
        setSuccessMessage("Payment Successful!");
        setLoading(false);
    } catch (error) {
        console.error("Payment error:", error);
        setErrorMessage(error.message || "An error occurred during payment.");
        setLoading(false);
    }
};


  // Define custom styles for the CardElement
  const cardStyle = {
    style: {
      base: {
        color: "#495057",
        fontFamily: '"Roboto", sans-serif',
        fontSmoothing: "antialiased",
        fontSize: "16px",
        "::placeholder": {
          color: "#adb5bd",
        },
      },
      invalid: {
        color: "#e63946",
        iconColor: "#e63946",
      },
    },
  };

  return (
    <div
      style={{
        maxWidth: "500px",
        margin: "50px auto",
        padding: "20px",
        borderRadius: "8px",
        backgroundColor: "#ffffff",
        boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
        border: "1px solid #dee2e6",
      }}
    >
      <h2
        style={{
          textAlign: "center",
          color: "#212529",
          fontWeight: "500",
          marginBottom: "20px",
        }}
      >
        Payment Details
      </h2>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handlePayment();
        }}
      >
        <div
          style={{
            border: "1px solid #ced4da",
            borderRadius: "8px",
            padding: "15px",
            marginBottom: "20px",
          }}
        >
          <CardElement options={cardStyle} />
        </div>
        <button
          type="submit"
          style={{
            width: "100%",
            padding: "12px",
            backgroundColor: "#007bff",
            color: "#ffffff",
            border: "none",
            borderRadius: "8px",
            fontSize: "16px",
            fontWeight: "500",
            cursor: "pointer",
          }}
          disabled={!stripe || loading}
        >
          {loading ? "Processing..." : "Pay Now"}
        </button>
      </form>
      {errorMessage && (
        <p
          style={{
            color: "#e63946",
            textAlign: "center",
            marginTop: "15px",
          }}
        >
          {errorMessage}
        </p>
      )}
      {successMessage && (
        <p
          style={{
            color: "#38b000",
            textAlign: "center",
            marginTop: "15px",
          }}
        >
          {successMessage}
        </p>
      )}
    </div>
  );
};

export default PaymentComponent;
